import * as Api from '@ViewModels';
import { action, computed, makeObservable, observable, override } from 'mobx';
import { AdminPermission, AdminUserSessionContext, AdminUserViewModel } from '../../viewmodels/AdminViewModels';
import { IFileSourceJobState, IIntegrationJobState } from '../models';

export class AdminAccountIntegrationsViewModel extends Api.AccountIntegrationsViewModel {
	constructor(userSession: Api.UserSessionContext, account: Api.IAccount) {
		super(userSession, account);

		makeObservable<AdminAccountIntegrationsViewModel, 'getIntegrationEnableAbilityRoute'>(this, {
			getIntegrationEnableAbilityRoute: action,
			getIntegrationEnableAbility: action,
		});

		this.mAccount = account;

		// @ts-ignore
		this.mAms360Vm = new AdminConfigurableIntegrationViewModel(
			userSession,
			account,
			Api.ConfigurableIntegrationType.Ams360
		);

		// @ts-ignore
		this.mHawksoftVm = new AdminConfigurableIntegrationViewModel(
			userSession,
			account,
			Api.ConfigurableIntegrationType.HawkSoft
		);

		// @ts-ignore
		this.mRedtailVm = new AdminConfigurableIntegrationViewModel(
			userSession,
			account,
			Api.ConfigurableIntegrationType.Redtail
		);

		// @ts-ignore
		this.mEclipseVm = new AdminConfigurableIntegrationViewModel(
			userSession,
			account,
			Api.ConfigurableIntegrationType.Eclipse
		);

		// @ts-ignore
		this.mClioVm = new AdminConfigurableIntegrationViewModel(
			userSession,
			account,
			Api.ConfigurableIntegrationType.Clio
		);

		// @ts-ignore
		this.mEzLynxVm = new AdminConfigurableIntegrationViewModel(
			userSession,
			account,
			Api.ConfigurableIntegrationType.EzLynx
		);

		// @ts-ignore
		this.mXanatekVm = new AdminConfigurableIntegrationViewModel(
			userSession,
			account,
			Api.ConfigurableIntegrationType.Xanatek
		);

		// @ts-ignore
		this.mEpicVm = new AdminConfigurableIntegrationViewModel(
			userSession,
			account,
			Api.ConfigurableIntegrationType.Epic
		);

		// @ts-ignore
		this.mQQVm = new AdminConfigurableIntegrationViewModel(userSession, account, Api.ConfigurableIntegrationType.QQ);

		// @ts-ignore
		this.mHubspotVm = new AdminConfigurableIntegrationViewModel(
			userSession,
			account,
			Api.ConfigurableIntegrationType.HubSpot
		);

		// @ts-ignore
		this.mSalesforceVm = new AdminConfigurableIntegrationViewModel(
			userSession,
			account,
			Api.ConfigurableIntegrationType.Salesforce
		);

		// @ts-ignore
		this.mWealthboxVm = new AdminConfigurableIntegrationViewModel(
			userSession,
			account,
			Api.ConfigurableIntegrationType.Wealthbox
		);

		// @ts-ignore
		this.mQBOVm = new AdminConfigurableIntegrationViewModel(
			userSession,
			account,
			Api.ConfigurableIntegrationType.QuickBooksOnline
		);

		// @ts-ignore
		this.mXeroVm = new AdminConfigurableIntegrationViewModel(
			userSession,
			account,
			Api.ConfigurableIntegrationType.Xero
		);

		// @ts-ignore
		this.mDonorPerfectVm = new AdminConfigurableIntegrationViewModel(
			userSession,
			account,
			Api.ConfigurableIntegrationType.DonorPerfect
		);
	}

	protected getLoadRoute() {
		return `impersonate/${this.account.id}/account/integrations`;
	}

	private getIntegrationEnableAbilityRoute(integrationProvider: Api.ConfigurableIntegrationType) {
		return `impersonate/${this.account.id}/account/integrations/${integrationProvider}/enableAbility`;
	}

	public getIntegrationEnableAbility(integrationProvider: Api.ConfigurableIntegrationType) {
		const providerToCheck = Api.resolveIntegrationProviderToApiType(integrationProvider);

		const adminUserSessionContext = this.userSession as AdminUserSessionContext;
		if (adminUserSessionContext.hasPermission(AdminPermission.ModifyAccount)) {
			const body: [] = [];
			return this.userSession.webServiceHelper.callAsync<string>(
				this.getIntegrationEnableAbilityRoute(providerToCheck),
				'GET',
				body
			);
		}
	}
}

export class AdminConfigurableIntegrationViewModel extends Api.ConfigurableIntegrationViewModel {
	// @ts-ignore
	protected mUsers: Api.ObservableCollection<AdminConfigurableIntegrationUserViewModel>;
	protected mRecentActivity: IIntegrationJobState[];
	protected loadingRecentActivity: boolean;

	constructor(
		userSession: Api.UserSessionContext,
		account: Api.IAccount,
		type: Api.ConfigurableIntegrationType,
		integration?: Api.IConfigurableIntegration
	) {
		super(userSession, account, type, integration);

		makeObservable<AdminConfigurableIntegrationViewModel, 'mRecentActivity' | 'loadingRecentActivity'>(this, {
			mRecentActivity: observable.ref,
			loadingRecentActivity: observable.ref,
			isLoadingRecentActivity: computed,
			users: override,
			recentActivity: computed,
			getUnmergeContactsRoute: action,
			getUnmergeContacts: action,
			setIntegration: override,
			reload: action,
			resync: action,
			loadRecentActivity: action,
			getIntegrationJobStateFileSourceDownloadUrl: action,
			setPreferredMappingKeyToResourceId: action,
		});

		this.mUsers = new Api.ObservableCollection<AdminConfigurableIntegrationUserViewModel>([], 'id');
	}

	public get isLoadingRecentActivity() {
		return this.loadingRecentActivity;
	}

	// @ts-ignore
	public get users() {
		return this.mUsers;
	}

	public get recentActivity() {
		return this.mRecentActivity;
	}

	public getUnmergeContactsRoute() {
		return `impersonate/${this.account.id}/contact/unmerge`;
	}

	public getUnmergeContacts() {
		const adminUserSessionContext = this.userSession as AdminUserSessionContext;
		if (
			adminUserSessionContext.hasPermission(AdminPermission.OnlyForDevelopers) ||
			adminUserSessionContext.hasPermission(AdminPermission.SupportLevel3)
		) {
			const body: [] = [];
			return this.userSession.webServiceHelper.callAsync<Api.IConfigurableIntegration>(
				this.getUnmergeContactsRoute(),
				'POST',
				body
			);
		}
	}

	public setIntegration(integration: Api.IConfigurableIntegration) {
		this.mUsers.clear();
		if (integration) {
			this.mUsers.addAll(
				(integration.userMap || []).map(x => {
					return new AdminConfigurableIntegrationUserViewModel(this.mUserSession, this.mAccount, x);
				})
			);
		}
		this.mIntegration = integration;
	}

	public async reload(contactId?: string, fields: string[] = []) {
		if (!this.isBusy) {
			let url = `impersonate/${
				this.mAccount.id
			}/admin/accountOperations/reloadIntegration?provider=${encodeURIComponent(this.apiType)}`;
			if (contactId) {
				url += `&contactIds=${contactId}`;
			}

			if (fields.length) {
				const params = fields.map(field => `fieldsToReload=${encodeURIComponent(field)}`);
				url += `&${params.join('&')}`;
			}
			return this.userSession.webServiceHelper.callAsync(url, 'GET');
		}
	}

	public resync(isFullResync?: boolean) {
		if (!this.isBusy) {
			const url = `admin/accountOperations/${this.mAccount.id}/extractIntegrations?provider=${encodeURIComponent(
				this.apiType
			)}&${isFullResync ? 'resync' : 'localResync'}=true`;
			return this.mUserSession.webServiceHelper.callAsync(url, 'GET');
		}
	}

	private getStateRouteBase() {
		return `admin/AccountIntegrationsOperations/${this.mAccount.id}/${encodeURIComponent(this.apiType)}/state`;
	}

	public loadRecentActivity = async () => {
		if (!this.loadingRecentActivity) {
			this.loadingRecentActivity = true;
			try {
				const value = await this.mUserSession.webServiceHelper.callAsync<IIntegrationJobState[]>(
					this.getStateRouteBase(),
					'GET'
				);

				this.mRecentActivity = value;
			} finally {
				this.loadingRecentActivity = false;
			}
		}
	};

	public getIntegrationJobStateFileSourceDownloadUrl = (
		jobState: IFileSourceJobState & IIntegrationJobState,
		fileSourceIndex: number
	) => {
		const route = `${this.getStateRouteBase()}/${encodeURIComponent(
			jobState.jobId
		)}/fileSources/downloadUrl?fileSourcePath=${encodeURIComponent(jobState.fileSources[fileSourceIndex].path)}`;
		return this.mUserSession.webServiceHelper.callAsync<string>(route, 'GET');
	};

	public setPreferredMappingKeyToResourceId() {
		const adminUserSessionContext = this.userSession as AdminUserSessionContext;
		if (adminUserSessionContext.hasPermission(AdminPermission.SupportLevel3)) {
			return this.userSession.webServiceHelper.callAsync<Api.IConfigurableIntegration>(
				this.setPreferredMappingKeyToResourceIdRoute(),
				'POST'
			);
		}
	}

	protected getConfigureRoute() {
		return `impersonate/${this.account.id}/account/integrations/configure/${this.apiType}`;
	}

	protected getLoadAccountRoute() {
		return `admin/account/${this.account.id}`;
	}

	protected getLoadIntegrationRoute() {
		return `impersonate/${this.account.id}/account/integrations/${this.apiType}`;
	}

	protected getSetUserMapRoute() {
		return `impersonate/${this.account.id}/account/integrations/${this.apiType}/userMap`;
	}

	protected getAvailableIntegrationTags() {
		return `impersonate/${this.account.id}/account/integrations/availableTags`;
	}

	protected setPreferredMappingKeyToResourceIdRoute() {
		return `impersonate/${this.account.id}/account/integrations/${this.apiType}/setPreferredMappingKeyToResourceId`;
	}
}

class AdminConfigurableIntegrationUserViewModel extends Api.ConfigurableIntegrationUserViewModel<AdminUserViewModel> {
	constructor(
		userSession: Api.UserSessionContext,
		account: Api.IAccount,
		integrationUser: Api.IIntegrationUserMapping
	) {
		super(userSession, account, integrationUser);

		this.mSetUser(integrationUser);
	}

	protected mSetUser(integrationUser: Api.IIntegrationUserMapping) {
		this.mIntegrationUser = integrationUser;

		this.levitateUser =
			!!integrationUser.levitateUser && !!integrationUser.levitateUser.id
				? new AdminUserViewModel(this.mUserSession as AdminUserSessionContext, integrationUser.levitateUser, {
						account: this.account,
					})
				: undefined;
	}
}
